import React, { FormEvent, useCallback, useEffect, useState } from "react";
import {
  BaseFee,
  feesApi,
  formatDay,
  formatRubles,
  GroupCurrentFee,
  GroupFeeEntry,
  todayISO,
} from "../api/fees";
import { useConfirm } from "../components/ConfirmDialog";
import PageLoader from "../components/PageLoader";
import "./FeesPage.css";

type PriceForm = { amount: string; from: string };
type GroupPriceForm = PriceForm & { groupID: string };

const EMPTY_PRICE: PriceForm = { amount: "", from: "" };
const EMPTY_GROUP_PRICE: GroupPriceForm = { ...EMPTY_PRICE, groupID: "" };

const PAST_DATE = "Дата не может быть раньше сегодняшней";

export default function FeesPage() {
  const [current, setCurrent] = useState<BaseFee | null>(null);
  const [history, setHistory] = useState<BaseFee[]>([]);
  const [groups, setGroups] = useState<GroupCurrentFee[]>([]);
  const [futures, setFutures] = useState<GroupFeeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [baseForm, setBaseForm] = useState<PriceForm>(EMPTY_PRICE);
  const [groupForm, setGroupForm] = useState<GroupPriceForm>(EMPTY_GROUP_PRICE);
  const [openGroupID, setOpenGroupID] = useState<string | null>(null);
  const [groupHistory, setGroupHistory] = useState<Record<string, GroupFeeEntry[]>>({});
  const { confirm, dialog } = useConfirm();

  const load = useCallback(async () => {
    try {
      const [cur, hist, groupList, futureList] = await Promise.all([
        feesApi.getBaseFee(),
        feesApi.listBaseFeeHistory(),
        feesApi.listGroupFees(),
        feesApi.listFutureGroupFees(),
      ]);
      setCurrent(cur);
      setHistory(hist ?? []);
      setGroups(groupList ?? []);
      setFutures(futureList ?? []);
      // A saved or deleted price makes a fetched group history stale.
      setGroupHistory({});
      setOpenGroupID(null);
    } catch (err: any) {
      console.error("Failed to load fees", err);
      setError(err?.message || "Не удалось загрузить цены");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const today = todayISO();

  const run = async (action: () => Promise<unknown>, failure: string, onSuccess?: () => void) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      onSuccess?.();
    } catch (err: any) {
      console.error("Fee action failed", err);
      setError(err?.message || failure);
    } finally {
      setBusy(false);
    }
    await load();
  };

  const submitBase = async (e: FormEvent) => {
    e.preventDefault();
    const amount = Number(baseForm.amount);
    if (busy || !(amount > 0)) return;
    if (baseForm.from && baseForm.from < today) {
      setError(PAST_DATE);
      return;
    }
    await run(
      () => feesApi.setBaseFee(amount, baseForm.from || undefined),
      "Не удалось сохранить базовую цену",
      () => setBaseForm(EMPTY_PRICE)
    );
  };

  const submitGroup = async (e: FormEvent) => {
    e.preventDefault();
    const amount = Number(groupForm.amount);
    if (busy || !groupForm.groupID || !(amount > 0)) return;
    if (groupForm.from && groupForm.from < today) {
      setError(PAST_DATE);
      return;
    }
    await run(
      () => feesApi.setGroupFee(groupForm.groupID, amount, groupForm.from || undefined),
      "Не удалось сохранить цену группы",
      () => setGroupForm(EMPTY_GROUP_PRICE)
    );
  };

  const deleteBase = async (fee: BaseFee) => {
    const confirmed = await confirm({
      title: "Удалить запланированную базовую цену?",
      body: (
        <p>
          {formatRubles(fee.monthly_fee)} с {formatDay(fee.effective_from)} не вступит в силу.{" "}
          {current
            ? `Базовой останется ${formatRubles(current.monthly_fee)}, пока не запланируете другую.`
            : "Базовая цена так и останется не заданной."}
        </p>
      ),
      confirmLabel: "Удалить",
      danger: true,
    });
    if (!confirmed) return;
    await run(() => feesApi.deleteFutureBaseFee(fee.id), "Не удалось удалить базовую цену");
  };

  const deleteGroupFee = async (group: GroupCurrentFee, fee: GroupFeeEntry) => {
    const confirmed = await confirm({
      title: `Удалить запланированную цену группы «${group.group_name}»?`,
      body: (
        <p>
          {formatRubles(fee.monthly_fee)} с {formatDay(fee.effective_from)} не вступит в силу. Группа останется на
          цене, которая действует сейчас.
        </p>
      ),
      confirmLabel: "Удалить",
      danger: true,
    });
    if (!confirmed) return;
    await run(() => feesApi.deleteFutureGroupFee(fee.id), "Не удалось удалить цену группы");
  };

  const toggleHistory = async (groupID: string) => {
    if (openGroupID === groupID) {
      setOpenGroupID(null);
      return;
    }
    setOpenGroupID(groupID);
    if (groupHistory[groupID]) return;
    try {
      const rows = await feesApi.listGroupFeeHistory(groupID);
      setGroupHistory((h) => ({ ...h, [groupID]: rows ?? [] }));
    } catch (err: any) {
      console.error("Failed to load group fee history", err);
      setError(err?.message || "Не удалось загрузить историю цен");
    }
  };

  if (loading) return <PageLoader />;

  const baseStatus = (fee: BaseFee): [string, string] => {
    if (fee.id === current?.id) return ["Действует сейчас", "mav-badge-active"];
    if (fee.effective_from > today) return ["Запланирована", "mav-badge-dirty"];
    return ["Была раньше", "mav-badge-muted"];
  };

  return (
    <div className="page page-wide">
      <h2>Стоимость</h2>
      <p className="page-description">
        Цена месяца тренировок. Счета по графику берут цену группы, а если у группы своей цены нет — базовую. Цену
        можно запланировать заранее и удалить, пока она не вступила в силу. Уже выставленные счета не меняются.
      </p>

      {error && (
        <div className="error" role="alert">
          {error}
        </div>
      )}

      <section className="fees-section">
        <h3>Базовая цена</h3>
        <div className="mav-card fees-current" role="region" aria-label="Базовая цена сейчас">
          {current ? (
            <>
              <strong className="fees-current-value">{formatRubles(current.monthly_fee)}</strong>
              <span className="fees-current-since">с {formatDay(current.effective_from)}</span>
            </>
          ) : (
            <>
              <strong className="fees-current-value">Не задана</strong>
              <span className="fees-current-since">
                Пока её нет, счета по графику получат только дети из групп со своей ценой.
              </span>
            </>
          )}
        </div>

        <form className="mav-card cp-form fees-form" aria-label="Новая базовая цена" onSubmit={submitBase}>
          <h4 className="fees-form-title">Новая базовая цена</h4>
          <label className="cp-field">
            <span>Цена в месяц, ₽</span>
            <input
              className="mav-input"
              type="number"
              min="1"
              step="0.01"
              inputMode="decimal"
              value={baseForm.amount}
              disabled={busy}
              onChange={(e) => setBaseForm((f) => ({ ...f, amount: e.target.value }))}
            />
          </label>
          <div className="cp-field">
            <label className="cp-field">
              <span>Действует с</span>
              <input
                className="mav-input"
                type="date"
                min={today}
                value={baseForm.from}
                disabled={busy}
                onChange={(e) => setBaseForm((f) => ({ ...f, from: e.target.value }))}
              />
            </label>
            <span className="mav-hint">Если пусто — с сегодня. Цена на ту же дату заменится.</span>
          </div>
          <div className="cp-actions mav-actions">
            <button type="submit" className="mav-btn-primary" disabled={busy || !(Number(baseForm.amount) > 0)}>
              Сохранить цену
            </button>
          </div>
        </form>

        <div className="table-wrap">
          <table className="settings-table stack-table fees-table fees-base-table" aria-label="Изменения базовой цены">
            <thead>
              <tr>
                <th>Действует с</th>
                <th>Цена</th>
                <th>Статус</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan={4} className="empty-cell">
                    {error ? "—" : "Базовую цену ещё не задавали"}
                  </td>
                </tr>
              ) : (
                history.map((fee) => {
                  const [label, badge] = baseStatus(fee);
                  const future = fee.id !== current?.id && fee.effective_from > today;
                  return (
                    <tr key={fee.id}>
                      <td className="cell-fee-date">{formatDay(fee.effective_from)}</td>
                      <td className="cell-fee-price">
                        <span className="fees-price">{formatRubles(fee.monthly_fee)}</span>
                      </td>
                      <td className="cell-fee-status">
                        <span className={`mav-badge ${badge}`}>{label}</span>
                      </td>
                      <td className="cell-action">
                        {future && (
                          <div className="row-actions">
                            <button className="btn-quiet-danger" disabled={busy} onClick={() => deleteBase(fee)}>
                              Удалить
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="fees-section">
        <h3>Цены групп</h3>
        <p className="page-description">Своя цена группы заменяет базовую для всех детей группы.</p>

        <form className="mav-card cp-form fees-form" aria-label="Новая цена группы" onSubmit={submitGroup}>
          <h4 className="fees-form-title">Новая цена группы</h4>
          <label className="cp-field">
            <span>Группа</span>
            <select
              className="mav-input"
              value={groupForm.groupID}
              disabled={busy}
              onChange={(e) => setGroupForm((f) => ({ ...f, groupID: e.target.value }))}
            >
              <option value="">Выберите группу</option>
              {groups.map((g) => (
                <option key={g.group_id} value={g.group_id}>
                  {g.group_name}
                </option>
              ))}
            </select>
          </label>
          <label className="cp-field">
            <span>Цена в месяц, ₽</span>
            <input
              className="mav-input"
              type="number"
              min="1"
              step="0.01"
              inputMode="decimal"
              value={groupForm.amount}
              disabled={busy}
              onChange={(e) => setGroupForm((f) => ({ ...f, amount: e.target.value }))}
            />
          </label>
          <div className="cp-field">
            <label className="cp-field">
              <span>Действует с</span>
              <input
                className="mav-input"
                type="date"
                min={today}
                value={groupForm.from}
                disabled={busy}
                onChange={(e) => setGroupForm((f) => ({ ...f, from: e.target.value }))}
              />
            </label>
            <span className="mav-hint">Если пусто — с сегодня. Цена на ту же дату заменится.</span>
          </div>
          <div className="cp-actions mav-actions">
            <button
              type="submit"
              className="mav-btn-primary"
              disabled={busy || !groupForm.groupID || !(Number(groupForm.amount) > 0)}
            >
              Сохранить цену группы
            </button>
          </div>
        </form>

        <div className="table-wrap">
          <table className="settings-table stack-table fees-table fees-group-table" aria-label="Цены групп">
            <thead>
              <tr>
                <th>Группа</th>
                <th>Цена сейчас</th>
                <th>Запланировано</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {groups.length === 0 ? (
                <tr>
                  <td colSpan={4} className="empty-cell">
                    {error ? "—" : "Групп пока нет"}
                  </td>
                </tr>
              ) : (
                groups.map((g) => {
                  const planned = futures.filter((f) => f.group_id === g.group_id);
                  const open = openGroupID === g.group_id;
                  const rows = groupHistory[g.group_id];
                  return (
                    <React.Fragment key={g.group_id}>
                      <tr>
                        <td className="cell-fee-group">{g.group_name}</td>
                        <td className="cell-fee-price">
                          {g.has_override && g.monthly_fee !== undefined ? (
                            <>
                              <span className="fees-price">{formatRubles(g.monthly_fee)}</span>
                              <span className="fees-sub">с {formatDay(g.effective_from)}</span>
                            </>
                          ) : (
                            <>
                              <span className="fees-price">{current ? formatRubles(current.monthly_fee) : "—"}</span>
                              <span className="fees-sub">Базовая цена</span>
                            </>
                          )}
                        </td>
                        <td className="cell-fee-planned">
                          {planned.length === 0 ? (
                            <span className="mav-status-muted">—</span>
                          ) : (
                            planned.map((f) => (
                              <div key={f.id} className="fees-planned">
                                <span>{`${formatRubles(f.monthly_fee)} с ${formatDay(f.effective_from)}`}</span>
                                <button
                                  className="btn-quiet-danger"
                                  disabled={busy}
                                  onClick={() => deleteGroupFee(g, f)}
                                >
                                  Удалить
                                </button>
                              </div>
                            ))
                          )}
                        </td>
                        <td className="cell-action">
                          <div className="row-actions">
                            <button
                              type="button"
                              className="mav-btn-secondary"
                              aria-expanded={open}
                              onClick={() => toggleHistory(g.group_id)}
                            >
                              {open ? "Скрыть историю" : "История"}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {open && (
                        <tr className="fees-history-row">
                          <td colSpan={4}>
                            {rows === undefined ? (
                              <span className="mav-status-muted">Загружаю историю…</span>
                            ) : rows.length === 0 ? (
                              <span className="mav-status-muted">Своей цены у группы не было</span>
                            ) : (
                              <ul className="fees-history" aria-label={`История цен группы «${g.group_name}»`}>
                                {rows.map((f) => (
                                  <li key={f.id}>
                                    <span>{`${formatRubles(f.monthly_fee)} с ${formatDay(f.effective_from)}`}</span>
                                    {f.effective_from > today && (
                                      <span className="mav-badge mav-badge-dirty">Запланирована</span>
                                    )}
                                    {g.has_override && f.effective_from === g.effective_from && (
                                      <span className="mav-badge mav-badge-active">Действует сейчас</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      {dialog}
    </div>
  );
}
